import React from 'react';
import { X } from 'lucide-react';
import Button from './ui/Button.tsx';

interface CardPrices {
  trendPrice?: number;
  averageSellPrice?: number;
  lowPrice?: number;
}

interface CardData {
  id: string;
  name: string;
  images?: { large?: string };
  hires_image?: string;
  image?: string;
  set?: { name?: string } | string;
  rarity?: string;
  hp?: string;
  cardmarket?: { prices?: CardPrices };
}

type CardStatus = 'owned' | 'wanted' | 'missing';

interface FancyCardModalProps {
  card: CardData | null;
  status?: CardStatus;
  variant?: string;
  onClose: () => void;
  onSetStatus: (card: CardData, status: CardStatus) => void;
}

export default function FancyCardModal({
  card,
  status,
  variant,
  onClose,
  onSetStatus
}: FancyCardModalProps) {
  if (!card) return null;

  const prices = card.cardmarket?.prices || null;
  const setName = typeof card.set === 'string' ? card.set : card.set?.name || '';
  const imageUrl = card.images?.large || card.hires_image || card.image || '/placeholder.svg';
  const displayPrice = prices?.trendPrice ?? prices?.averageSellPrice ?? prices?.lowPrice ?? 0;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center p-4 z-[2000]"
      onClick={onClose}
    >
      {/* Close Button */}
      <Button
        variant="ghost"
        onClick={onClose}
        className="absolute top-8 right-8 z-50 rounded-full p-3"
      >
        <X className="w-6 h-6" />
      </Button>

      {/* Card + Details Container */}
      <div className="relative w-full max-w-md mx-auto" onClick={(e) => e.stopPropagation()}>
        {/* Floating Card */}
        <div
          className="relative w-full rounded-2xl border-4 border-white shadow-2xl overflow-hidden animate-card-float"
          style={{
            aspectRatio: '3 / 4',
            backgroundImage: 'linear-gradient(to bottom right, rgba(255,255,255,0.15), rgba(255,255,255,0))',
            backgroundColor: 'rgba(0,0,0,0.2)',
          }}
        >
          {/* High-res Card Image */}
          <div className="absolute inset-3 bg-white rounded-xl overflow-hidden">
            <img
              src={imageUrl}
              alt={card.name}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          {/* Holographic Effect */}
          <div
            className="absolute inset-0 opacity-50 pointer-events-none animate-shimmer"
            style={{
              background: `
                radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9) 0%, transparent 50%),
                radial-gradient(circle at 70% 70%, rgba(255,255,255,0.7) 0%, transparent 50%),
                linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.8) 50%, transparent 70%),
                linear-gradient(-45deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%)
              `,
            }}
          />

          {/* Rainbow overlay for holographic effect */}
          <div
            className="absolute inset-0 opacity-30 pointer-events-none animate-rainbow animate-holographic mix-blend-overlay"
          />

          {/* Price Chip */}
          {prices && displayPrice > 0 && (
            <div
              className="absolute bottom-4 right-4 bg-green-600 text-white text-lg px-3 py-2 rounded-lg font-bold shadow-glow-green"
            >
              €{displayPrice.toFixed(2)}
            </div>
          )}
        </div>

        {/* Info Panel */}
        <div className="mt-4 bg-black/60 text-white p-4 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold drop-shadow-lg">{card.name}</h3>
              <p className="text-sm opacity-90">
                {setName} • {card.rarity || '—'} {card.hp ? `• ${card.hp} HP` : ''}
                {variant ? ` • ${variant}` : ''}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={status === 'owned' ? 'success' : 'ghost'}
                size="sm"
                onClick={() => onSetStatus(card, 'owned')}
              >
                Owned
              </Button>
              <Button
                variant={status === 'wanted' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => onSetStatus(card, 'wanted')}
                className={status === 'wanted' ? 'bg-amber-500 hover:bg-amber-400' : ''}
              >
                Wanted
              </Button>
              <Button
                variant={status === 'missing' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => onSetStatus(card, 'missing')}
              >
                Missing
              </Button>
            </div>
          </div>

          {/* Prices Grid */}
          {prices && (
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="bg-white/10 rounded-md p-2">
                <div className="text-xs opacity-80">Avg. Sell</div>
                <div className="font-semibold">€{prices.averageSellPrice?.toFixed(2) || '—'}</div>
              </div>
              <div className="bg-white/10 rounded-md p-2">
                <div className="text-xs opacity-80">Low</div>
                <div className="font-semibold">€{prices.lowPrice?.toFixed(2) || '—'}</div>
              </div>
              <div className="bg-white/10 rounded-md p-2">
                <div className="text-xs opacity-80">Trend</div>
                <div className="font-semibold">€{prices.trendPrice?.toFixed(2) || '—'}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
